import React, { useRef, useState, useEffect } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import LocationInputForm from "../LocationInput/LocationInputForm";
import { CKEditor5Editor } from "../CKEditor/CKEditor5Editor";

const OrganizationEditForm = () => {
  const isAdmin= true;
  const { id } = useParams();
  const previousContentRef = useRef("");

  const [organization, setOrganization] = useState({
    image: null,
    name: "",
    email: "",
    description: "",
    location: "",
    lat: "",
    lng: "",
    radius: "",
    password: "",
    socials: [],
    donationLink: "",
    website: "",
    initiatives: [],
    openToNewMembers: false,
    howToJoin: "",
    verified: false, // Admin controlled field
  });

  const [social, setSocial] = useState({ platform: "", link: "" });
  const [initiative, setInitiative] = useState({
    title: "",
    img: null,
    description: "",
    link: "",
  });

  const [imageFile, setImageFile] = useState(null);
  const [errors, setErrors] = useState({});
  const maxWords = 1000;

  useEffect(() => {
    const fetchOrganization = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_URLVAR}/api/general/organizations/${id}`,
          {
            headers: {
              "x-auth-token": localStorage.getItem("Token"),
            },
          }
        );

        setOrganization((prev) => ({
          ...prev, // Keep existing values
          ...response.data, // Update with fetched values
        }));
      } catch (error) {
        console.error("Error fetching organization:", error);
      }
    };

    fetchOrganization();
  }, [id]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setOrganization({ ...organization, [name]: value });
  };

  const handleFileChange = (e) => {
    setImageFile(e.target.files[0]);
  };

  const handleInitiativeFileChange = (e) => {
    setInitiative({ ...initiative, img: e.target.files[0] });
  };

  const handleSocialChange = (e) => {
    const { name, value } = e.target;
    setSocial({ ...social, [name]: value });
  };

  const countWords = (text) => text.split(/\s+/).filter((word) => word.length > 0).length;

  const handleCKEditorChange = (e, editor) => {
    const newValue = editor.getData();
    const wordCount = countWords(newValue);

    if (wordCount <= maxWords) {
      setOrganization({ ...organization, description: newValue });
      previousContentRef.current = newValue;
    } else {
      alert(`Word limit exceeded! Maximum allowed words are ${maxWords}.`);
      editor.setData(previousContentRef.current);
    }
  };

  const addSocial = () => {
    if (social.platform && social.link) {
      setOrganization({ ...organization, socials: [...organization.socials, social] });
      setSocial({ platform: "", link: "" });
    } else {
      alert("Please fill in both platform and link for social media!");
    }
  };

  const deleteSocial = (index) => {
    const updatedSocials = organization.socials.filter((_, i) => i !== index);
    setOrganization({ ...organization, socials: updatedSocials });
  };

  const handleInitiativeChange = (e) => {
    const { name, value } = e.target;
    setInitiative({ ...initiative, [name]: value });
  };

  const addInitiative = () => {
    if (initiative.title && initiative.img && initiative.description && initiative.link) {
      setOrganization({
        ...organization,
        initiatives: [...organization.initiatives, { ...initiative, img: null }],
      });
      setInitiative({ title: "", img: null, description: "", link: "" });
    } else {
      alert("Please fill in all fields for the initiative!");
    }
  };

  const deleteInitiative = (index) => {
    const updatedInitiatives = organization.initiatives.filter((_, i) => i !== index);
    setOrganization({ ...organization, initiatives: updatedInitiatives });
  };

  const handleLocationSelect = (location) => {
    setOrganization({
      ...organization,
      location: `${location.city}, ${location.state}, ${location.country}`,
      lat: `${location.latitude}`,
      lng: `${location.longitude}`,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const formData = new FormData();
    Object.keys(organization).forEach((key) => {
      if (key === "image") {
        formData.append(key, organization[key]);
      } else if (key === "initiatives") {
        formData.append("initiatives", JSON.stringify(organization.initiatives));
      } else if (key === "socials") {
        formData.append("socials", JSON.stringify(organization.socials));
      } else {
        formData.append(key, organization[key]);
      }
    });

    // Append initiative images
    organization.initiatives.forEach((initiative, index) => {
      if (initiative.img) {
        formData.append(`initiatives[${index}][img]`, initiative.img);
      }
    });

    try {
      await axios.put(
        `${process.env.REACT_APP_URLVAR}/api/general/organizations/${id}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            "x-auth-token": localStorage.getItem("Token"),
          },
        }
      );
      alert("Edited Successfully");
    } catch (err) {
      console.error(err);
      alert("Error saving organization");
    }
  };

  return (
    <div className="container mt-5">
      <h2>Edit Organization</h2>
      <form onSubmit={handleSubmit}>
        <div className="mb-3">
          <label className="form-label">Organization Logo</label>
          <input type="file" className="form-control" name="image" onChange={handleFileChange} />
        </div>

        <div className="mb-3">
          <label className="form-label">Organization Name</label>
          <input type="text" className="form-control" name="name" value={organization.name} onChange={handleInputChange} required />
        </div>

        <div className="mb-3">
          <label className="form-label">Description</label>
          <CKEditor5Editor value={organization.description} onChange={handleCKEditorChange} />
        </div>
        <div className="mb-3">
          <label className="form-label">Radius (in miles)</label>
          <input type="number" className="form-control" name="radius" value={organization.radius} onChange={handleInputChange} required />
        </div>
        <div className="mb-3">
          <label className="form-label">Location</label>
          <LocationInputForm onLocationSelect={handleLocationSelect} />
        </div>

        <div className="mb-3">
          <label className="form-label">Social Links</label>
          <div className="input-group">
            <select className="form-select" name="platform" value={social.platform} onChange={handleSocialChange}>
              <option value="">Select Platform</option>
              <option value="linkedin">LinkedIn</option>
              <option value="instagram">Instagram</option>
              <option value="facebook">Facebook</option>
              <option value="twitter">Twitter</option>
            </select>
            <input type="text" className="form-control" placeholder="Link" name="link" value={social.link} onChange={handleSocialChange} />
            <button type="button" className="btn btn-secondary" onClick={addSocial}>Add</button>
          </div>
        </div>

        <div className="mb-3">
          <label className="form-label">Initiatives</label>
          <input type="text" className="form-control" placeholder="Title" name="title" value={initiative.title} onChange={handleInitiativeChange} />
          <input type="file" className="form-control mt-2" name="img" onChange={handleInitiativeFileChange} />
        </div>

        <div className="mb-3 form-check">
          <input type="checkbox" className="form-check-input" id="verified" name="verified" checked={organization.verified} onChange={(e) => isAdmin && setOrganization({ ...organization, verified: e.target.checked })} disabled={!isAdmin} />
          <label htmlFor="verified" className="form-check-label">Verified (Admin Only)</label>
        </div>

        <button type="submit" className="btn btn-success">Save</button>
      </form>
    </div>
  );
};

export default OrganizationEditForm;
